import { useEffect } from 'react'
import { useWeb3React } from '@web3-react/core'
import { useAppDispatch } from 'state/hooks'
import { setNativeCurrencyPrice } from './reducer'
import { COINGECKO_NATIVE_IDS, PRICE_CACHE_MS, fetchNativeCurrencyPrice, STABLECOIN_NATIVE_CHAINS } from 'constants/coingecko'
import store from 'state'

const PRICE_REFRESH_INTERVAL_MS = PRICE_CACHE_MS // 3 minutes

// Module-level state - survives React StrictMode remounts
const fetchingChains = new Set<number>()

export default function ExploreUpdater(): null {
  const { chainId } = useWeb3React()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!chainId) return

    // Handle stablecoin native chains - set $1 without API call
    if (STABLECOIN_NATIVE_CHAINS.has(chainId)) {
      dispatch(
        setNativeCurrencyPrice({
          chainId,
          price: { priceUsd: 1, lastUpdated: Date.now() },
        })
      )
      return
    }

    const currencyId = COINGECKO_NATIVE_IDS[chainId]
    if (!currencyId) return

    const fetchPrice = async () => {
      // Prevent concurrent fetches for this chain
      if (fetchingChains.has(chainId)) return

      // Read cache directly from store
      const state = store.getState()
      const lastUpdated = state.explore?.nativeCurrencyPrice[chainId]?.lastUpdated ?? 0

      // Skip if cache is fresh (less than 3 minutes old)
      if (Date.now() - lastUpdated < PRICE_CACHE_MS) {
        return
      }

      fetchingChains.add(chainId)
      try {
        const price = await fetchNativeCurrencyPrice(currencyId)
        dispatch(
          setNativeCurrencyPrice({
            chainId,
            price: { priceUsd: price, lastUpdated: Date.now() },
          })
        )
      } catch (err) {
        console.error('Failed to refresh price:', err)
      } finally {
        fetchingChains.delete(chainId)
      }
    }

    // Initial fetch (only if cache is stale)
    fetchPrice()

    // Set up polling interval
    const intervalId = setInterval(fetchPrice, PRICE_REFRESH_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
    }
  }, [chainId, dispatch])

  return null
}
